import { httpGetJson, httpPutJson } from '@/lib/net'

import { getUserId } from '@/services/identity-client'
import type { StorageRecords } from '@onxmaps/layer-management'
import { captureException } from '@sentry/vue'
import throttle from 'lodash/throttle'

let storageKey: string

/**
 * *(AI-generated)*
 * Initializes the persistence service for layer management settings.
 */
export const initializePersistence = async () => {
  const app = 'fish'
  const userId = await getUserId()
  storageKey = `onx.${app}.${userId}.layer-settings`

  await initializeCloudStorage()

  // If page is hidden, sync to prevent data from being lost.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      updateLayerSettings()
    }
  })
}

/**
 * Saves user layer settings to cloud persistence.  If not settings are passed,
 * current user layer settings will be persisted from local storage.
 * @param settings Optional settings
 */
export const updateLayerSettings = (settings?: StorageRecords) => {
  if (!settings) {
    settings = getSettings()
  }

  // ensure there is at least one layer, tag, or activity to save, otherwise there is nothing to persist
  if (
    Object.keys(settings.layers).length === 0 &&
    Object.keys(settings.tags).length === 0 &&
    Object.keys(settings.activities).length === 0
  ) {
    return
  }

  try {
    // using keepalive to ensure request completes on navigation or close
    const url = `${window.environment.cartoUrl}/layer-management/v1/layer-settings`
    const fetchOptions: RequestInit = { keepalive: true }
    httpPutJson(url, settings, fetchOptions)
  } catch (err) {
    captureException(err as Error, {
      tags: {
        lms: 'update layer settings failed',
      },
    })
  }
}

const initializeCloudStorage = async () => {
  try {
    const url = `${window.environment.cartoUrl}/layer-management/v1/layer-settings`
    const cloudData = await httpGetJson<StorageRecords>(url)

    // if no data in cloud persistence or invalid data structure
    if (
      !cloudData ||
      (!cloudData.layers &&
        !cloudData.tags &&
        !cloudData.activities &&
        !cloudData.difficulties)
    ) {
      return
    }

    const localData = getSettings()
    const newSettings: StorageRecords = {
      layers: { ...(cloudData.layers || {}), ...localData.layers },
      tags: { ...(cloudData.tags || {}), ...localData.tags },
      activities: { ...(cloudData.activities || {}), ...localData.activities },
      difficulties: {
        ...(cloudData.difficulties || {}),
        ...localData.difficulties,
      },
    }

    localStorage.setItem(storageKey, JSON.stringify(newSettings))
  } catch (err) {
    captureException(err as Error, {
      tags: {
        lms: 'get layer settings failed',
      },
    })
  }
}

// throttle calls, to keep from hammering cloud persistence
const updateLayerSettingsThrottled = throttle(updateLayerSettings, 2000)

/**
 * Saves user layer settings to both local storage and sync with cloud storage.
 * @param settings layer settings
 */
export const saveSettings = (settings: StorageRecords) => {
  localStorage.setItem(storageKey, JSON.stringify(settings))
  updateLayerSettingsThrottled(settings)
}

/**
 * Gets user layer settings.
 */
export const getSettings = (): StorageRecords => {
  const settings: StorageRecords = {
    layers: {},
    tags: {},
    activities: {},
    difficulties: {},
  }
  const existingSettings: Partial<StorageRecords> = JSON.parse(
    localStorage.getItem(storageKey) || '{}'
  )

  // merge any local storage records into new object.
  if (existingSettings.layers) {
    settings.layers = existingSettings.layers
  }
  if (existingSettings.tags) {
    settings.tags = existingSettings.tags
  }
  if (existingSettings.activities) {
    settings.activities = existingSettings.activities
  }
  if (existingSettings.difficulties) {
    settings.difficulties = existingSettings.difficulties
  }

  return settings
}
